/**
 * Sky-view LUT parameterisation (Hillaire 2020): non-linear latitude that packs texels near the horizon, and
 * absolute scene-frame azimuth (u = 0 is -Z, the camera azimuth; u = 0.25 is +X). The GLSL in
 * atmosphereShaders.ts implements the same maths; this TS copy feeds the CPU sky-light (SH) projection and tests.
 */
import type { Vec3 } from '../frame';
import { ATMOSPHERE } from './atmosphere';

const TAU = Math.PI * 2;

function angles(camRadiusM: number): { beta: number; zenithHorizon: number } {
  const beta = Math.acos(Math.sqrt(Math.max(0, camRadiusM * camRadiusM - ATMOSPHERE.groundRadiusM ** 2)) / camRadiusM);
  return { beta, zenithHorizon: Math.PI - beta };
}

export function skyViewUvToDir(u: number, v: number, camRadiusM: number): Vec3 {
  const { beta, zenithHorizon } = angles(camRadiusM);
  let zen: number;
  if (v < 0.5) {
    const c = 1 - 2 * v;
    zen = zenithHorizon * (1 - c * c);
  } else {
    const c = 2 * v - 1;
    zen = zenithHorizon + beta * c * c;
  }
  const phi = u * TAU;
  const s = Math.sin(zen);
  return [s * Math.sin(phi), Math.cos(zen), -s * Math.cos(phi)];
}

export function dirToSkyViewUv(d: Vec3, camRadiusM: number): [number, number] {
  const { beta, zenithHorizon } = angles(camRadiusM);
  const zen = Math.acos(Math.min(1, Math.max(-1, d[1])));
  let v: number;
  if (zen <= zenithHorizon) {
    v = (1 - Math.sqrt(Math.max(0, 1 - zen / zenithHorizon))) / 2;
  } else {
    v = 0.5 + Math.sqrt(Math.min(1, (zen - zenithHorizon) / beta)) / 2;
  }
  let u = Math.atan2(d[0], -d[2]) / TAU;
  if (u < 0) u += 1;
  return [u, v];
}
